import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Heart,
  BookOpen,
  Users,
  Ear,
  Handshake,
  ShieldCheck,
  CheckCircle2,
  Compass,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

const PILLARS = [
  {
    icon: Users,
    title: "See the Person",
    body:
      "Practice attention that goes beyond diagnosis, prognosis, and paperwork — noticing the patient, the family, and the story behind the bedside.",
  },
  {
    icon: Ear,
    title: "Hear the Voice",
    body:
      "Learn how to listen without agenda so patients and loved ones have space to say what they need to say — including things they may not feel ready or able to share elsewhere.",
  },
  {
    icon: Compass,
    title: "Honor the Journey",
    body:
      "Walk with people through decline, decision-making, farewells, and grief in a way that is patient-centered, belief-respectful, and never rushed.",
  },
];

const APPLIED_FOCUS = [
  "Applying From Diagnosis to Goodbye principles at the bedside",
  "Presence with patients through cognitive, physical, and emotional decline",
  "Supporting compassionate conversations around patient values, end-of-life wishes, and goals of care within the chaplain's role",
  "Supporting family systems through anticipatory grief",
  "Working alongside hospice interdisciplinary teams (nursing, social work, medical, aides)",
  "Recognizing when a family needs silence, ritual, prayer, or simply company",
  "Cultural and belief-system sensitivity in hospice contexts",
  "Boundaries between chaplain care, clinical care, and appropriate referral to other disciplines or community spiritual supports",
  "Documentation and communication appropriate to hospice practice",
  "Bereavement follow-up and post-death family support",
  "Sustaining the chaplain: compassion fatigue, grief accumulation, and self-care",
];

const WHO_ITS_FOR = [
  {
    title: "Hospice and palliative-care chaplains",
    body: "Currently serving in hospice or palliative settings and wanting to sharpen applied, patient-centered chaplaincy skills.",
  },
  {
    title: "Pastors and ministers preparing for hospice work",
    body: "Called to walk with the dying and their families, and looking for a practical framework beyond the pulpit.",
  },
  {
    title: "Spiritual-care providers in senior living, memory care, or home health",
    body: "Regularly encountering end-of-life realities and seeking a hospice-informed way of showing up.",
  },
  {
    title: "Experienced chaplains extending into hospice",
    body: "Bringing established chaplaincy or ministry experience into a specialty pathway focused on the end-of-life journey.",
  },
];

const NOT_ABOUT = [
  "A general workplace chaplaincy course",
  "A book study or reading group",
  "A preaching or sermon-preparation course",
  "A theological or systematic-theology course",
];

export default function AcademyHospiceChaplaincy() {
  usePageMeta({
    title: "Track Three: Hospice Chaplaincy | CCCHAPS Chaplain Training Academy",
    description:
      "An applied hospice chaplaincy specialty track within the CCCHAPS Chaplain Training Academy. Practicing From Diagnosis to Goodbye — see the person, hear the voice, honor the journey.",
  });

  return (
    <div data-testid="academy-hospice-track" className="bg-white">
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F0F4F8] via-[#F8FAFC] to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <Link
            to="/academy"
            data-testid="hospice-track-back-to-academy"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Chaplain Training Academy
          </Link>

          <div className="max-w-3xl flex flex-col items-start gap-5">
            <span
              data-testid="hospice-track-badge"
              className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-800"
            >
              Book-Based Specialty Track
            </span>
            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 font-semibold">
              CCCHAPS Academy · Track Three
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              Hospice Chaplaincy
            </h1>
            <p className="text-lg sm:text-xl text-slate-700 italic leading-relaxed">
              Practicing From Diagnosis to Goodbye
            </p>
            <p
              data-testid="hospice-track-tagline"
              className="text-sm sm:text-base uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold"
            >
              See the Person. Hear the Voice. Honor the Journey.
            </p>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
              Track Three is an applied hospice chaplaincy pathway for pastors, chaplains, ministers, and spiritual-care providers who want to take the principles presented in <em>From Diagnosis to Goodbye</em> and learn how to practice them in real hospice ministry.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/books/from-diagnosis-to-goodbye"
                data-testid="hospice-track-book-link"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] shadow-sm"
              >
                Read the Foundational Book <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hospice-track-contact-link"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
              >
                Contact CCCHAPS About Track Three
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- HOSP-001 FREE PREVIEW ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="hospice-track-hosp001-section">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-white to-[#F0F4F8] p-8 md:p-10">
          <div className="flex flex-col gap-3 max-w-3xl">
            <span data-testid="hospice-hosp001-badge" className="self-start inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-800">Free Track Three Preview Course</span>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Start With HOSP-001</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
              See the Person First
            </h2>
            <p className="text-lg text-slate-700 italic">An Introduction to Patient-Centered Hospice Chaplaincy</p>
            <p className="text-slate-600 leading-relaxed">
              Experience the patient-centered teaching approach behind Track Three through this free introductory course. Learn why hospice chaplaincy begins by seeing the person, hearing the patient's voice, and understanding the experience before deciding how to respond.
            </p>
            <p className="text-sm text-slate-500 font-medium" data-testid="hospice-hosp001-meta">
              35–45 minutes · Online · Self-Paced · Free
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              <Link
                to="/academy/portal/hosp-001"
                data-testid="hospice-hosp001-start"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] shadow-sm"
              >
                START FREE HOSP-001 COURSE <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/academy/portal/hosp-001"
                data-testid="hospice-hosp001-details"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
              >
                COURSE DETAILS
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- OPTIONAL LIVE DISCUSSION (informational) ---------------- */}
      <section className="bg-white border-y border-slate-200" data-testid="hospice-live-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <div className="grid md:grid-cols-[auto_1fr] items-start gap-6 md:gap-8">
            <div
              aria-hidden="true"
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#F0F4F8] border border-slate-200 text-[#4A6FA5] shrink-0"
            >
              <Compass className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="max-w-3xl">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Optional Live Discussion</p>
              <h2 className="mt-1 font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
                From the Book to the Bedside — Live
              </h2>
              <p className="mt-3 text-slate-700 leading-relaxed">
                From the Book to the Bedside — Live gives HOSP-001 learners an opportunity to participate in interactive online discussions with Chaplain Derrick Hall, including hospice case scenarios, questions, and practical application of the patient-centered principles introduced in the course.
              </p>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Live sessions are optional. HOSP-001 remains fully self-paced and can be completed without attending a live session.
              </p>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                Future session dates and access information are provided to enrolled HOSP-001 learners when available. Start HOSP-001 for free to receive session updates.
              </p>
              <div className="mt-5">
                <Link
                  to="/academy/portal/hosp-001"
                  data-testid="hospice-live-start-hosp"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
                >
                  Start Free HOSP-001 <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- THREE-PILLAR FRAMEWORK ---------------- */}
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20"
        data-testid="hospice-track-pillars"
      >
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
            The Track Three posture
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
            From the book to the bedside.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Track Three trains a way of being present with the dying and their families that translates a hospice chaplaincy philosophy into everyday practice. It is not a theology course, not a preaching course, and not a book study — it is applied hospice ministry.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              data-testid={`hospice-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-sm transition-all"
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-4">
                <p.icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 leading-tight">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- APPLIED FOCUS ---------------- */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-1">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
                Applied hospice practice
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
                Skills for the end-of-life journey.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Track Three focuses on what hospice chaplaincy actually looks like — moment by moment — from admission through the final visit and the family's grief that follows.
              </p>
            </div>
            <div className="lg:col-span-2">
              <ul
                className="grid sm:grid-cols-2 gap-x-8 gap-y-3"
                data-testid="hospice-track-applied-focus"
              >
                {APPLIED_FOCUS.map((area) => (
                  <li
                    key={area}
                    className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed"
                  >
                    <CheckCircle2
                      className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5"
                      strokeWidth={2}
                    />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- WHO IT IS FOR ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
            Who Track Three is for
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
            An applied specialty pathway.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {WHO_ITS_FOR.map((w) => (
            <div
              key={w.title}
              data-testid={`hospice-who-${w.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-sm transition-all"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-3">
                <Heart className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 leading-tight">{w.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- WHAT TRACK THREE IS NOT ---------------- */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
                Scope
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900 leading-tight">
                What Track Three is not.
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Naming the scope helps applicants find the right fit. Track Three is a focused, applied hospice chaplaincy pathway.
              </p>
            </div>
            <ul
              className="space-y-2.5"
              data-testid="hospice-track-not-list"
            >
              {NOT_ABOUT.map((n) => (
                <li
                  key={n}
                  className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed rounded-lg bg-white border border-slate-200 px-4 py-3"
                >
                  <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED: PVC-001 (INTERDISCIPLINARY) ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-16">
        <div
          className="rounded-2xl border border-[#4A6FA5]/25 bg-gradient-to-br from-white via-[#F0F4F8] to-white p-8 md:p-10"
          data-testid="hospice-track-pvc-featured"
        >
          <div className="grid md:grid-cols-[1.35fr_1fr] gap-8 md:gap-12 items-start">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">
                For the Interdisciplinary Team
              </p>
              <h2
                className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight"
                data-testid="hospice-track-pvc-heading"
              >
                The Patient&rsquo;s Voice in Care
              </h2>
              <p className="mt-2 font-heading text-xl text-slate-700 leading-tight">
                Bringing the Patient&rsquo;s Voice Into Hospice and Healthcare Practice
              </p>
              <p className="mt-5 text-slate-700 leading-relaxed">
                <strong className="text-slate-900">Looking for training for the entire care team?</strong>{" "}
                The Patient&rsquo;s Voice in Care is a five-session interdisciplinary course based on{" "}
                <em>From Diagnosis to Goodbye</em>. It helps healthcare and hospice professionals strengthen patient-centered communication, preserve dignity, recognize what may be happening beneath patient and family interactions, and keep the person&rsquo;s voice present throughout serious illness, end-of-life care, and transition into grief.
              </p>

              <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="hospice-track-pvc-status">
                <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800">
                  In Development
                </span>
                <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                  Not Yet Enrolling
                </span>
              </div>

              <Link
                to="/academy/patients-voice-in-care"
                data-testid="hospice-track-pvc-cta"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
              >
                Explore The Patient&rsquo;s Voice in Care <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="space-y-5">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                  Who it is for
                </p>
                <p className="text-sm text-slate-700 leading-relaxed" data-testid="hospice-track-pvc-audience">
                  Nurses · Social Workers · Hospice Aides · Physicians / Providers · Chaplains · Care Coordinators · Volunteers · Bereavement Staff · Healthcare Leaders
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                  Course journey
                </p>
                <ol className="space-y-1.5 text-sm text-slate-800" data-testid="hospice-track-pvc-journey">
                  <li className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Step 1</span> <span className="text-slate-400">·</span> <span className="font-medium">See the Person</span></li>
                  <li className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Step 2</span> <span className="text-slate-400">·</span> <span className="font-medium">Hear the Voice</span></li>
                  <li className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Step 3</span> <span className="text-slate-400">·</span> <span className="font-medium">Honor What Matters</span></li>
                  <li className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Step 4</span> <span className="text-slate-400">·</span> <span className="font-medium">Stay Present</span></li>
                  <li className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Step 5</span> <span className="text-slate-400">·</span> <span className="font-medium">Carry the Voice Forward</span></li>
                </ol>
              </div>
            </div>
          </div>
          <p className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
            Distinct from Track Three Hospice Chaplaincy. The Patient&rsquo;s Voice in Care is patient-centered professional education for the broader healthcare and hospice team; Track Three remains the deeper specialty pathway for chaplains and spiritual-care providers. Both are connected through the principles and foundational text <em>From Diagnosis to Goodbye</em>.
          </p>
        </div>
      </section>

      {/* ---------------- BOOK CROSS-LINK ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F0F4F8] via-white to-white p-8 md:p-10 grid md:grid-cols-[auto_1fr_auto] items-center gap-6 md:gap-8"
          data-testid="hospice-track-book-callout"
        >
          <div
            aria-hidden="true"
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white border border-slate-200 text-[#4A6FA5] shadow-sm shrink-0"
          >
            <BookOpen className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">
              Foundational text
            </p>
            <h3 className="mt-1 font-heading text-2xl text-slate-900 leading-tight">
              From Diagnosis to Goodbye
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
              Track Three is anchored in the principles presented in the book. The book alone will move you; Track Three helps you practice those principles in real hospice ministry.
            </p>
          </div>
          <Link
            to="/books/from-diagnosis-to-goodbye"
            data-testid="hospice-track-book-callout-cta"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] whitespace-nowrap"
          >
            Read the Book <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ---------------- SCOPE NOTE ---------------- */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed" data-testid="hospice-track-scope-note">
              Track Three is offered within the CCCHAPS Chaplain Training Academy as a specialty pathway. HOSP-001 is currently available as a free, self-paced introduction to the Track Three approach. Additional Track Three training opportunities, schedules, formats, and enrollment details will be announced as they become available. Track Three does not carry certification, continuing-education credit, licensure, or accreditation, and completion does not guarantee employment or placement.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-5">
          <Handshake className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
          Interested in the Hospice Chaplaincy Track?
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Reach out to the CCCHAPS team to talk about Track Three, or explore the broader Chaplain Training Academy.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            data-testid="hospice-track-final-contact"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] shadow-sm"
          >
            Contact CCCHAPS About Track Three <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/academy"
            data-testid="hospice-track-final-academy"
            className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
          >
            Explore the Chaplain Training Academy
          </Link>
        </div>
      </section>
    </div>
  );
}
